import * as Sentry from '@sentry/react';
import {
    capitalize,
    filterGameRelatedAttributes,
    getDeviceRam,
    getGameBuildVersion,
    upper,
} from 'App/Helpers';
import { getAdConfig } from 'App/Services/Ads/helpers';
import { UAParser } from 'ua-parser-js';

/**
 * Bacanagol-style mapping: nests `game-*` under `game`, maps `video:*`, build fields, and GA lists.
 * Differs from {@link getGameInitValues} by preserving raw `game-` suffix keys under `game`.
 *
 * @param {Object<string, *>} apiConfig - Dynamic config from Sharpstar `getOffers`
 * @returns {Object<string, *>} Shape with optional `game`, `videos`, `buildVersion`, `buildIteration`, `currencies`, `itemTypes`
 */
export const normalizeRemoteConfig = (apiConfig) => {
    return Object.entries(apiConfig).reduce((result, [key, value]) => {
        if (key.startsWith('game-')) {
            if (key === 'game-silence-logs') result.silenceLogs = value;

            result.game = {
                ...(result.game ?? {}),
                [key.slice('game-'.length)]: value,
            };
        } else if (key.startsWith('video:')) {
            result.videos = {
                ...(result.videos ?? {}),
                [key.slice('video:'.length)]: value,
            };
        } else if (key === 'build-version') {
            result.buildVersion = value;
        } else if (key === 'build-iteration') {
            result.buildIteration = value;
        } else if (key === 'ga-currencies') {
            result.currencies = value?.split(',');
        } else if (key === 'ga-item-types') {
            result.itemTypes = value?.split(',');
        }

        return result;
    }, {});
};

/**
 * Bacanagol LP variant of {@link getGameLpConfigs}: uses {@link normalizeRemoteConfig} for non-default offers
 * and flattens config into `fallbackConfig` / `primaryConfig` + optional URL addressables.
 *
 * @param {Object} params
 * @param {{ id: number, content: Object }} params.offer
 * @param {Object} params.appConfig
 * @param {Object} params.adDefaultMap
 * @param {*} params.defaultVideos
 * @param {*} params.gaCustomDimensions
 *
 * @returns {{
 *   landingVideos: *,
 *   leadData: Object<string, string>,
 *   newGaConfig: Object,
 *   newAdValues: Object,
 *   newGameConfig: Object
 * }}
 */
export const createLpConfigs = ({
    offer,
    appConfig,
    adDefaultMap,
    defaultVideos,
    gaCustomDimensions,
}) => {
    const {
        env,
        domain,
        frontVersion,
        adStaticConfig,
        activeLanguages,
        gaTrackId,
        gameAnalyticsKey,
        gameAnalyticsSecret,
    } = appConfig;
    const { id, content } = offer;
    const buildPath = `${domain}${env === 'production' ? '/game/' : '/build/'}`;

    let sentryTagBuild, gaCurrencies, gaItemTypes;
    let newAdValues, newGameConfig, newGaConfig, landingVideos;

    if (id === 0) {
        const {
            BuildVersion,
            BuildIteration,
            GaCurrencies,
            GaItemTypes,
            ...rest
        } = content;
        const buildPathAddition = BuildIteration
            ? `${BuildVersion}/${BuildIteration}`
            : BuildVersion;

        landingVideos = defaultVideos;
        gaCurrencies = GaCurrencies?.split(',');
        gaItemTypes = GaItemTypes?.split(',');
        sentryTagBuild = buildPathAddition;

        newAdValues = {
            ...adDefaultMap,
            ...adStaticConfig,
        };
        newGameConfig = {
            silenceLogs: 'true',
            buildVersion: BuildVersion,
            buildUrl: `${buildPath}${buildPathAddition}/`,
            fallbackConfig: rest,
        };
    } else {
        const {
            videos,
            currencies,
            itemTypes,
            silenceLogs,
            buildVersion,
            buildIteration,
            game,
        } = normalizeRemoteConfig(content);
        const adConfig = getAdConfig(content);
        const { baseBuild, buildPathAddition, isBuildFromUrl } =
            getGameBuildVersion(buildVersion, buildIteration);

        landingVideos = videos;
        gaCurrencies = currencies;
        gaItemTypes = itemTypes;
        sentryTagBuild = buildPathAddition;
        newAdValues = {
            ...adConfig,
            ...adStaticConfig,
        };
        newGameConfig = {
            buildVersion: baseBuild,
            buildUrl: `${buildPath}${buildPathAddition}/`,
            ActiveLanguages: activeLanguages.map(upper).join(','),
            primaryConfig: game,
            silenceLogs,
            ...(isBuildFromUrl && {
                addressablesFromUrl: {
                    AddressablesSettingsPath: `${buildPath}${buildPathAddition}`,
                    AddressablesBundlePath: `${domain}/bundle/${buildPathAddition}/WebGL`,
                },
            }),
        };
    }
    newGaConfig = {
        dimensions: gaCustomDimensions,
        resources: { currencies: gaCurrencies, itemTypes: gaItemTypes },
        gameAnalyticsKey,
        gameAnalyticsSecret,
        gaTrackId,
    };

    const leadData = filterGameRelatedAttributes(content);

    Sentry.setTags({
        'game-build': sentryTagBuild,
        'front-build': frontVersion,
    });

    return {
        landingVideos,
        leadData,
        newGaConfig,
        newAdValues,
        newGameConfig,
    };
};

/**
 * Converts a single kebab-case key to PascalCase (first segment capitalized).
 * "shot-streak" -> "ShotStreak"
 *
 * @param {string} key
 * @returns {string}
 */
const normalize = (key) =>
    key.replace(/(^|-)([a-z])/g, (_, __, letter) => upper(letter));

/**
 * Maps flat `primaryConfig` keys into nested `ShotStreakConfiguration`, `push`, `pwa`, or top-level PascalCase.
 *
 * @param {Object<string, *>} rest - Raw `primaryConfig` object from `normalizeRemoteConfig`
 * @returns {Object<string, *>}
 */
const mapPrimaryConfig = (rest) =>
    Object.entries(rest).reduce((result, [key, value]) => {
        const assignNested = (targetKey, rawKey, prefix) => {
            const normalizedKey = normalize(rawKey.slice(prefix.length));

            result[targetKey] = {
                ...(result[targetKey] ?? {}),
                [normalizedKey]: value,
            };
        };

        if (key.startsWith('shot-streak-')) {
            assignNested('ShotStreakConfiguration', key, 'shot-streak-');
        } else if (key.startsWith('push-')) {
            assignNested('push', key, 'push-');
        } else if (key.startsWith('pwa-')) {
            assignNested('pwa', key, 'pwa-');
        } else {
            result[normalize(key)] = value;
        }

        return result;
    }, {});

/**
 * Builds the object later JSON-stringified by {@link serializeGameInitConfig}: default-offer merge or
 * Bacanagol primary config + optional URL addressables + `OfferConfiguration`.
 *
 * @param {Object} config - `newGameConfig` from {@link createLpConfigs}
 * @param {string} locale
 * @param {string} pwaStatus
 * @param {string} pushStatus
 * @returns {Object}
 */
const createGameInitConfig = (config, locale, pwaStatus, pushStatus) => {
    const { number: deviceMemory } = getDeviceRam();
    const { name } = new UAParser().getBrowser();
    const {
        ActiveLanguages,
        addressablesFromUrl,
        primaryConfig,
        fallbackConfig,
    } = config;
    const baseConfig = {
        ActiveLanguages,
        Language: upper(locale),
        DeviceMemory: deviceMemory,
        BrowserInfo: name?.replace(/\s+/g, '') || 'Undefined',
    };

    if (fallbackConfig) return { ...baseConfig, ...fallbackConfig };

    const { push, pwa, ...normalizedConfig } = mapPrimaryConfig(primaryConfig);

    return {
        ...baseConfig,
        ...normalizedConfig,
        ...addressablesFromUrl,
        OfferConfiguration: {
            PWAState: pwaStatus,
            PushState: capitalize(pushStatus),
            OfferDisplayConfigurations: [
                { ...push, OfferType: 'Push' },
                { ...pwa, OfferType: 'PWA' },
            ].filter(Boolean),
        },
    };
};

/**
 * JSON-stringifies the payload produced by `createGameInitConfig` (same argument list).
 *
 * @param args
 * @returns {string} JSON string for the game bootstrap
 */
export const serializeGameInitConfig = (...args) =>
    JSON.stringify(createGameInitConfig(...args));
